import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { cls } from "../hatim/soft/SoftKit";
import QuestionRows from "./QuestionRows";
import QuestionEditorDialog from "./QuestionEditorDialog";
import { LIMITS } from "./questionRules";

const linkCls = `flex items-center gap-1 text-sm ${cls.accent} disabled:opacity-40`;

/**
 * Soru listesi düzenleyicisi: seçili paket satırı, "Kütüphaneden seç",
 * "Yeni soru", "Paket olarak kaydet" bağlantıları ve düzenlenebilir satırlar.
 * Müzakere sihirbazı ve soru düzenleme kullanır; paket düzenleyicisi
 * onPickPackage vermeden (kütüphane bağlantısız) kullanabilir; onSearchQuestions
 * verilirse "Diğer paketlerden soru ara" bağlantısı çıkar.
 */
export default function QuestionsSection({
  questions,
  onChange,
  disabled,
  packageTitle,
  onRemovePackage,
  onPickPackage,
  onSaveAsPackage,
  packageSaved,
  onSearchQuestions,
}) {
  const { t } = useScriptMode();
  // { index: null } yeni soru, { index: n } düzenleme
  const [editing, setEditing] = useState(null);
  const full = questions.length >= LIMITS.questionsMax;
  const editingQuestion = editing && editing.index !== null ? questions[editing.index] || null : null;

  return (
    <div className="flex flex-col gap-3">
      {packageTitle ? (
        <div className={`flex items-center gap-3 py-2 ${cls.row}`}>
          <MenuBookOutlinedIcon fontSize="small" className={cls.accent} />
          <div className="min-w-0 flex-1">
            <div className={`text-xs ${cls.muted}`}>{t("mzq_selected_package")}</div>
            <div className={`text-sm line-clamp-2 ${cls.title}`}>{packageTitle}</div>
          </div>
          {onRemovePackage ? (
            <button type="button" className={`text-sm ${cls.accent}`} onClick={onRemovePackage} disabled={disabled}>
              {t("mzq_remove_package")}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {onPickPackage ? (
          <button type="button" className={linkCls} onClick={onPickPackage} disabled={disabled}>
            <CollectionsBookmarkOutlinedIcon fontSize="small" />
            {t("mzq_pick_from_library")}
          </button>
        ) : null}
        {onSearchQuestions ? (
          <button type="button" className={linkCls} onClick={onSearchQuestions} disabled={disabled || full}>
            <SearchIcon fontSize="small" />
            {t("lib_search_questions")}
          </button>
        ) : null}
        <button
          type="button"
          className={linkCls}
          onClick={() => setEditing({ index: null })}
          disabled={disabled || full}
        >
          <AddIcon fontSize="small" />
          {t("mzq_new_question")}
        </button>
        {onSaveAsPackage ? (
          <button type="button" className={linkCls} onClick={onSaveAsPackage} disabled={disabled}>
            <SaveOutlinedIcon fontSize="small" />
            {t("mzq_save_as_package")}
          </button>
        ) : null}
      </div>
      {packageSaved && questions.length > 0 ? (
        <p className={`text-xs ${cls.muted}`}>{t("mzq_package_saved")}</p>
      ) : null}

      {questions.length === 0 ? (
        <p className={`text-sm ${cls.muted}`}>{t("mzq_no_questions_hint")}</p>
      ) : (
        <div className="flex flex-col gap-1">
          <span className={`text-xs self-end ${cls.muted}`}>{`${questions.length}/${LIMITS.questionsMax}`}</span>
          <QuestionRows
            questions={questions}
            showIssues
            disabled={disabled}
            onEdit={(index) => setEditing({ index })}
            onRemove={(index) => onChange(questions.filter((_, i) => i !== index))}
          />
        </div>
      )}

      <QuestionEditorDialog
        open={!!editing}
        initial={editingQuestion}
        onClose={() => setEditing(null)}
        onSave={(q) => {
          onChange(
            editing && editing.index !== null
              ? questions.map((x, i) => (i === editing.index ? q : x))
              : [...questions, q].slice(0, LIMITS.questionsMax)
          );
          setEditing(null);
        }}
      />
    </div>
  );
}
